import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  Plus,
  Search,
  Send,
  Inbox,
  Printer,
  CheckCircle,
  XCircle,
  Clock,
  Stethoscope,
  Trash2,
  Link2,
  MessageSquare,
  ArrowUpRight,
} from "@/components/icons";
import ReferralModal from "../../components/ReferralModal";
import ReferralPatientModal from "../../components/ReferralPatientModal";
import { TableSkeleton } from "../../components/Skeletons";
import EmptyState from "../../components/EmptyState";
import { doctorLabel } from "../../lib/doctorLabel";
import AppleKpi from "../../components/AppleKpi";

const STATUS_LABELS = {
  pendiente: { label: "Pendiente", color: "bg-amber-50 text-amber-700", icon: Clock },
  aceptado: { label: "Aceptado", color: "bg-indigo-50 text-indigo-700", icon: CheckCircle },
  completado: { label: "Completado", color: "bg-emerald-50 text-emerald-700", icon: CheckCircle },
  rechazado: { label: "Rechazado", color: "bg-red-50 text-red-700", icon: XCircle },
};

const URGENCY_STYLES = {
  urgente: "bg-red-50 text-red-700",
  rutina: "bg-[#F2F3F5] text-black/60",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildWaUrl(r) {
  const phone = (r.external_whatsapp || "").replace(/[^\d]/g, "");
  const url = `${window.location.origin}/r/${r.public_token}`;
  const msg = encodeURIComponent(
    `Hola${
      r.external_doctor_name ? ` ${r.external_doctor_name}` : ""
    }, te comparto un referido médico del paciente ${r.patient_name}. ` +
      `Motivo: ${r.reason}. Puedes verlo aquí: ${url}`
  );
  return `https://wa.me/${phone}?text=${msg}`;
}

export default function Referrals() {
  const { user } = useAuth();
  const [box, setBox] = useState("sent"); // "sent" | "received"
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Chip rápido: "", "pendientes", "urgentes", "completados"
  const [quickFilter, setQuickFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [patientModal, setPatientModal] = useState(null);
  const [threadModal, setThreadModal] = useState(null);
  const [pendingReceivedCount, setPendingReceivedCount] = useState(0);

  const refreshPendingReceived = () => {
    api
      .get("/referrals?box=received&status=pendiente")
      .then((r) =>
        setPendingReceivedCount(Array.isArray(r.data) ? r.data.length : 0)
      )
      .catch(() => {});
  };
  useEffect(() => {
    refreshPendingReceived();
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ box });
    if (statusFilter) params.set("status", statusFilter);
    api
      .get(`/referrals?${params.toString()}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [box, statusFilter]);

  const filtered = useMemo(() => {
    let list = items;
    // Chip "urgentes" filtra en cliente (urgency != status)
    if (quickFilter === "urgentes") {
      list = list.filter((r) => r.urgency === "urgente");
    }
    if (!q.trim()) return list;
    const needle = q.toLowerCase();
    return list.filter(
      (r) =>
        (r.patient_name || "").toLowerCase().includes(needle) ||
        (r.reason || "").toLowerCase().includes(needle) ||
        (r.to_doctor_name || "").toLowerCase().includes(needle) ||
        (r.from_doctor_name || "").toLowerCase().includes(needle) ||
        (r.external_specialty || "").toLowerCase().includes(needle)
    );
  }, [items, q, quickFilter]);

  const changeStatus = async (id, status, note = null) => {
    try {
      await api.patch(`/referrals/${id}/status`, { status, note });
      toast.success("Estado actualizado");
      load();
      refreshPendingReceived();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const removeRef = async (id) => {
    if (!window.confirm("¿Eliminar este referido? Sólo es posible si está pendiente.")) return;
    try {
      await api.delete(`/referrals/${id}`);
      toast.success("Referido eliminado");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const pendientes = items.filter((r) => r.status === "pendiente").length;
    const completados = items.filter((r) => r.status === "completado").length;
    const rechazados = items.filter((r) => r.status === "rechazado").length;
    const urgentes = items.filter((r) => r.urgency === "urgente").length;
    return { total, pendientes, completados, rechazados, urgentes };
  }, [items]);

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="referrals-page">
      {/* ── KPI ROW ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RefDonut total={stats.total} completados={stats.completados} />
        <PendientesSpectrum pendientes={stats.pendientes} total={stats.total} />
        <UrgentesCard urgentes={stats.urgentes} total={stats.total} />
        <RechazadosBar rechazados={stats.rechazados} total={stats.total} />
      </div>

      {/* ── LIST CARD ───────────────────────────────────────────────── */}
      <div
        className="bg-white rounded-xl border border-black/[0.05] p-4 flex-1 flex flex-col min-h-0"
        data-testid="referrals-list-card"
      >
        <div className="flex flex-col mb-3 gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              {[
                { k: "sent", label: "Enviados", icon: Send },
                { k: "received", label: "Recibidos", icon: Inbox },
              ].map((t) => {
                const active = box === t.k;
                const showBadge = t.k === "received" && pendingReceivedCount > 0;
                return (
                  <button
                    key={t.k}
                    onClick={() => setBox(t.k)}
                    data-testid={`tab-${t.k}`}
                    className={`h-9 px-3 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 transition ${
                      active
                        ? "bg-[#0071E3] text-white"
                        : "bg-white border border-black/[0.08] text-black hover:bg-black/[0.03]"
                    }`}
                  >
                    <t.icon className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                    {t.label}
                    {showBadge && (
                      <span
                        data-testid="tab-received-badge"
                        className={`min-w-[16px] h-4 px-1 rounded text-[9px] font-bold inline-flex items-center justify-center ${
                          active ? "bg-white text-[#0071E3]" : "bg-red-500 text-white"
                        }`}
                      >
                        {pendingReceivedCount > 9 ? "9+" : pendingReceivedCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-full sm:w-64 bg-white border border-black/[0.08] rounded-lg flex items-center px-3 h-9 focus-within:border-black/40 transition">
                <Search className="w-4 h-4 text-black/40 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                <input
                  data-testid="ref-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Paciente, motivo, doctor…"
                  className="flex-1 bg-transparent px-2 outline-none text-[13px] text-black placeholder-black/40 min-w-0"
                />
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                data-testid="new-referral-btn"
                className="h-9 pl-2.5 pr-3 rounded-lg bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition flex-shrink-0"
              >
                <Plus className="w-4 h-4" strokeWidth={2} weight="regular" /> Nuevo referido
              </button>
            </div>
          </div>

          {/* Chips de filtro rápido */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { k: "", label: "Todos", status: "" },
              { k: "pendientes", label: "Pendientes", status: "pendiente" },
              { k: "urgentes", label: "Urgentes", status: "" },
              { k: "completados", label: "Completados", status: "completado" },
            ].map((chip) => {
              const active = quickFilter === chip.k;
              return (
                <button
                  key={chip.k || "all"}
                  type="button"
                  data-testid={`ref-chip-${chip.k || "all"}`}
                  onClick={() => {
                    setQuickFilter(chip.k);
                    setStatusFilter(chip.status);
                  }}
                  className={`h-8 px-3 rounded-full text-[12px] font-semibold transition ${
                    active
                      ? "bg-[#0071E3] text-white"
                      : "bg-white border border-black/[0.08] text-black hover:bg-black/[0.03]"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
            <TableSkeleton rows={6} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={<Stethoscope className="w-8 h-8 text-[#0071E3]" strokeWidth={1.75} weight="regular" />}
              title={
                box === "sent"
                  ? "No has enviado referidos aún"
                  : "No has recibido referidos"
              }
              description={
                box === "sent"
                  ? "Refiere pacientes a otros especialistas para coordinar su atención."
                  : "Aquí aparecerán los pacientes que otros doctores te refieran."
              }
              ctaLabel={box === "sent" ? "Crear el primero" : undefined}
              onCtaClick={box === "sent" ? () => setModalOpen(true) : undefined}
              testid="referrals-empty"
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
            <table className="w-full text-sm" data-testid="referrals-table">
              <thead className="sticky top-0 bg-[#F5F5F7] z-[1]">
                <tr className="text-left">
                  {[
                    "Fecha",
                    "Paciente",
                    box === "sent" ? "Destino" : "De",
                    "Motivo",
                    "Prioridad",
                    "Estado",
                    "",
                  ].map((h) => (
                    <th
                      key={h || "actions"}
                      className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <ReferralRow
                    key={r.id}
                    r={r}
                    box={box}
                    currentUserId={user.id}
                    onChangeStatus={changeStatus}
                    onDelete={removeRef}
                    onOpenPatient={() => setPatientModal(r)}
                    onOpenThread={() => setThreadModal(r)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <ReferralModal
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}
      {patientModal && (
        <ReferralPatientModal
          referral={patientModal}
          onClose={() => setPatientModal(null)}
          onSaved={load}
        />
      )}
      {threadModal && (
        <SenderThreadModal
          referral={threadModal}
          onClose={() => setThreadModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ─── KPI Widgets ──────────────────────────────────────────────────────────

function RefDonut({ total, completados }) {
  const pct = total ? Math.round((completados / total) * 100) : 0;
  return (
    <AppleKpi
      testid="ref-kpi-total"
      icon={Send}
      title="Total referidos"
      description="Pacientes derivados a otros especialistas."
      label="En el flujo"
      value={total}
      footer={total === 0 ? "Sin referidos aún" : `${pct}% completados`}
    />
  );
}

function PendientesSpectrum({ pendientes, total }) {
  const pct = total ? Math.round((pendientes / total) * 100) : 0;
  return (
    <AppleKpi
      testid="ref-kpi-pendientes"
      icon={Clock}
      title="Pendientes"
      description="Referidos aún sin respuesta del especialista destino."
      label="Sin resolver"
      value={pendientes}
      footer={total === 0 ? "Sin pendientes" : `${pct}% del total`}
    />
  );
}

function UrgentesCard({ urgentes, total }) {
  const pct = total ? Math.round((urgentes / total) * 100) : 0;
  return (
    <AppleKpi
      testid="ref-kpi-urgentes"
      icon={Stethoscope}
      title="Urgentes"
      description="Casos marcados como prioritarios por severidad clínica."
      label="Para priorizar"
      value={urgentes}
      footer={total === 0 ? "Sin urgentes" : `${pct}% del total`}
    />
  );
}

function RechazadosBar({ rechazados, total }) {
  const pct = total ? Math.round((rechazados / total) * 100) : 0;
  return (
    <AppleKpi
      testid="ref-kpi-rechazados"
      icon={XCircle}
      title="Rechazados"
      description="Referidos no aceptados por el especialista destino."
      label="No aceptados"
      value={rechazados}
      footer={total === 0 ? "Sin rechazos" : `${pct}% del total`}
    />
  );
}

function ReferralRow({ r, box, currentUserId, onChangeStatus, onDelete, onOpenPatient, onOpenThread }) {
  const status = STATUS_LABELS[r.status] || STATUS_LABELS.pendiente;
  const StatusIcon = status.icon;
  const isSender = r.from_doctor_id === currentUserId;
  const isReceiver = r.to_doctor_id === currentUserId;
  const isExternal = !r.to_doctor_id;

  const targetName = isExternal
    ? r.external_doctor_name || r.external_specialty || "Especialista externo"
    : doctorLabel(r.to_doctor_name || "—");
  const targetSub = isExternal
    ? [r.external_specialty, r.external_center].filter(Boolean).join(" · ")
    : r.to_doctor_specialty || "";

  const fromName = doctorLabel(r.from_doctor_name || "—");
  const fromSub = r.from_doctor_specialty || "";

  const patientInitials = (r.patient_name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <tr
      data-testid={`referral-row-${r.id}`}
      className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
    >
      {/* Fecha */}
      <td className="py-3 pr-3 whitespace-nowrap">
        <div className="text-[13px] font-semibold text-black">
          {fmtDate(r.created_at)}
        </div>
      </td>
      {/* Paciente */}
      <td className="py-3 px-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#F2F3F5] text-black text-[12px] font-bold flex items-center justify-center flex-shrink-0">
            {patientInitials}
          </div>
          {isReceiver ? (
            <button
              type="button"
              onClick={onOpenPatient}
              data-testid={`open-patient-${r.id}`}
              className="font-semibold truncate max-w-[200px] text-left text-[14px] text-black hover:underline"
              title="Ver datos primordiales del paciente"
            >
              {r.patient_name || "Paciente"}
            </button>
          ) : r.patient_id ? (
            <Link
              to={`/doctor/pacientes/${r.patient_id}`}
              className="font-semibold truncate max-w-[200px] text-[14px] text-black hover:underline"
            >
              {r.patient_name || "Paciente"}
            </Link>
          ) : (
            <span className="font-semibold truncate max-w-[200px] text-[14px] text-black">
              {r.patient_name || "Paciente"}
            </span>
          )}
        </div>
      </td>
      {/* Destino / De */}
      <td className="py-3 px-3 max-w-[220px]">
        <div className="font-semibold truncate text-[13px] text-black">
          {box === "sent" ? targetName : fromName}
        </div>
        {(box === "sent" ? targetSub : fromSub) && (
          <div className="text-[11px] text-black/50 truncate">
            {box === "sent" ? targetSub : fromSub}
          </div>
        )}
        {box === "sent" && isExternal && (
          <span className="mt-1 inline-block px-2 py-0.5 rounded-md text-[9px] uppercase tracking-wider font-semibold bg-[#F2F3F5] text-black/70">
            Externo
          </span>
        )}
      </td>
      {/* Motivo */}
      <td className="py-3 px-3 max-w-[240px]">
        <div className="text-[13px] text-black/85 line-clamp-2">{r.reason}</div>
        {isExternal && r.external_confirmed_at && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
            <CheckCircle className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Recibido
          </div>
        )}
        {(box === "sent" && (r.unread_for_sender || 0) > 0) ||
        (box === "received" && (r.unread_for_receiver || 0) > 0) ? (
          <button
            type="button"
            onClick={box === "sent" ? onOpenThread : onOpenPatient}
            data-testid={`ref-thread-badge-${r.id}`}
            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500 text-white text-[10px] font-semibold hover:bg-red-600 transition"
          >
            <MessageSquare className="w-3 h-3" strokeWidth={1.75} weight="regular" />
            {box === "sent"
              ? `${r.unread_for_sender} solicitud${r.unread_for_sender > 1 ? "es" : ""}`
              : `${r.unread_for_receiver} respuesta${r.unread_for_receiver > 1 ? "s" : ""}`}
          </button>
        ) : (r.info_thread || []).length > 0 ? (
          <button
            type="button"
            onClick={box === "sent" ? onOpenThread : onOpenPatient}
            data-testid={`ref-thread-open-${r.id}`}
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-black/60 hover:text-black transition"
          >
            <MessageSquare className="w-3 h-3" strokeWidth={1.75} weight="regular" />
            Ver hilo ({r.info_thread.length})
          </button>
        ) : null}
      </td>
      {/* Prioridad */}
      <td className="py-3 px-3">
        <span
          className={`px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-semibold ${
            URGENCY_STYLES[r.urgency] || URGENCY_STYLES.rutina
          }`}
        >
          {r.urgency === "urgente" ? "Urgente" : "Rutina"}
        </span>
      </td>
      {/* Estado */}
      <td className="py-3 px-3">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-semibold ${status.color}`}
        >
          <StatusIcon className="w-3 h-3" strokeWidth={1.75} weight="regular" />
          {status.label}
        </span>
      </td>
      {/* Acciones */}
      <td className="py-3 pl-3 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          <Link
            to={`/referidos/${r.id}/imprimir`}
            target="_blank"
            data-testid={`ref-print-${r.id}`}
            title="Imprimir / PDF"
            className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-black/5 text-black/70 transition"
          >
            <Printer className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </Link>
          {isExternal && r.public_token && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/r/${r.public_token}`
                );
                toast.success("Enlace copiado");
              }}
              data-testid={`ref-copy-${r.id}`}
              title="Copiar enlace público"
              className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-black/5 text-black/70 transition"
            >
              <Link2 className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          )}
          {isExternal && r.public_token && r.external_whatsapp && (
            <button
              type="button"
              onClick={() => window.open(buildWaUrl(r), "_blank", "noopener,noreferrer")}
              data-testid={`ref-wa-${r.id}`}
              title="Enviar por WhatsApp"
              className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-emerald-50 text-emerald-600 transition"
            >
              <Send className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          )}
          {isReceiver && r.status === "pendiente" && (
            <>
              <button
                onClick={() => onChangeStatus(r.id, "aceptado")}
                data-testid={`ref-accept-${r.id}`}
                className="px-2.5 py-1 rounded-md bg-black hover:bg-black/85 text-white text-[10px] uppercase font-semibold transition"
              >
                Aceptar
              </button>
              <button
                onClick={() => {
                  const note = window.prompt("Motivo del rechazo (opcional):");
                  if (note !== null) onChangeStatus(r.id, "rechazado", note);
                }}
                data-testid={`ref-reject-${r.id}`}
                className="px-2.5 py-1 rounded-md bg-white border border-black/10 hover:bg-black/[0.04] text-black text-[10px] uppercase font-semibold transition"
              >
                Rechazar
              </button>
            </>
          )}
          {isReceiver && r.status === "aceptado" && (
            <button
              onClick={() => onChangeStatus(r.id, "completado")}
              data-testid={`ref-complete-${r.id}`}
              className="px-2.5 py-1 rounded-md bg-black hover:bg-black/85 text-white text-[10px] uppercase font-semibold transition"
            >
              Completar
            </button>
          )}
          {isSender && isExternal && r.status === "pendiente" && (
            <button
              onClick={() => onChangeStatus(r.id, "completado")}
              className="px-2.5 py-1 rounded-md bg-black hover:bg-black/85 text-white text-[10px] uppercase font-semibold transition"
            >
              Completar
            </button>
          )}
          {isSender && r.status === "pendiente" && (
            <button
              onClick={() => onDelete(r.id)}
              data-testid={`ref-delete-${r.id}`}
              title="Eliminar"
              className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-red-50 text-black/50 hover:text-red-500 transition"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}



function SenderThreadModal({ referral, onClose, onSaved }) {
  const [thread, setThread] = useState(referral.info_thread || []);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if ((referral.unread_for_sender || 0) > 0) {
      api
        .post(`/referrals/${referral.id}/mark-thread-read`)
        .then(() => onSaved?.())
        .catch(() => {});
    }
  }, [referral.id, referral.unread_for_sender, onSaved]);

  const send = async () => {
    if (msg.trim().length < 3) {
      toast.error("Escribe la respuesta (mínimo 3 caracteres)");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/referrals/${referral.id}/reply-info`, {
        message: msg.trim(),
      });
      toast.success("Respuesta enviada");
      if (data?.entry) setThread((t) => [...t, data.entry]);
      setMsg("");
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#171717] rounded-3xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sender-thread-modal"
      >
        <div className="px-6 pt-5 pb-3 border-b border-black/[0.06] dark:border-white/[0.08]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40">
            Solicitud de información
          </div>
          <div className="text-xl font-extrabold text-black dark:text-white mt-0.5">
            {referral.patient_name}
          </div>
          <div className="text-xs text-black/60 dark:text-white/60 mt-1">
            Solicitada por {doctorLabel(referral.to_doctor_name)}
            {referral.to_doctor_specialty
              ? ` · ${referral.to_doctor_specialty}`
              : ""}
          </div>
        </div>

        <div className="p-6 space-y-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40">
            Motivo del referido
          </div>
          <div className="bg-black/[0.03] dark:bg-white/[0.04] rounded-xl p-3 text-sm text-black dark:text-white">
            {referral.reason}
          </div>

          <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40 pt-2">
            Conversación
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {thread.length === 0 ? (
              <div className="text-xs text-black/50 dark:text-white/50 text-center py-4">
                Sin mensajes aún.
              </div>
            ) : (
              thread.map((m) => {
                const isReceiver = m.from === "receiver";
                return (
                  <div
                    key={m.id}
                    className={`p-3 rounded-2xl text-sm ${
                      isReceiver
                        ? "bg-amber-50 dark:bg-amber-500/10 text-black dark:text-white"
                        : "bg-blue-50 dark:bg-[#01BC8E]/10 text-black dark:text-white"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-black/50 dark:text-white/50 mb-1">
                      {isReceiver ? "Solicitud" : "Tu respuesta"} · {doctorLabel(m.from_doctor_name || "")}
                      {" · "}
                      {new Date(m.created_at).toLocaleString("es-DO", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div className="whitespace-pre-line">{m.message}</div>
                  </div>
                );
              })
            )}
          </div>

          <div>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={3}
              placeholder="Escribe la información solicitada…"
              data-testid="ref-reply-textarea"
              className="w-full px-4 py-2.5 rounded-xl border border-black/[0.10] dark:border-white/[0.10] bg-white dark:bg-[#0d0d0d] text-sm text-black dark:text-white placeholder-black/40 dark:placeholder-white/40 resize-none"
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-sm font-bold text-black/60 dark:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
              >
                Cerrar
              </button>
              <button
                onClick={send}
                disabled={sending}
                data-testid="ref-reply-send"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#01BC8E] hover:bg-[#019A75] text-white text-sm font-bold shadow-lg shadow-emerald-500/30 disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? "Enviando…" : "Enviar respuesta"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
